import React, {useEffect, useMemo, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';
import {Button, EMPHASIS, FlexRow, SearchField, Table, Tabs, useTableFunctions} from '@forecast-it/design-system';
import {PageHeader} from '@/web-components/core/page-header';
import Styled from 'styled-components';
import CustomScrollDiv from '../shared/components/scroll-bars/custom_scroll_div';
import TheEye from '../shared/components/headers/header-bar/TheEye';
import {hasPermission, hasSomePermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import emptyStateBackground from '../../images/empty-states/empty-state-background-2.png';
import {
	showCreateOpportunityModal,
	type CreateOpportunityCreatedNode,
} from '../shared/components/modals/create-opportunity/CreateOpportunityModal';
import {
	OPPORTUNITY_FILTER_TABS,
	OPPORTUNITY_TAB_MESSAGE_IDS,
	OPPORTUNITY_EMPTY_MESSAGE_IDS,
	filterOpportunitiesByTab,
	matchesOpportunitySearch,
	buildOpportunityFilterPredicate,
	readSessionTab,
	writeSessionTab,
	readSessionSort,
	writeSessionSort,
	resolveInitialTab,
	DEFAULT_PREFERENCE_SCOPE,
	type OpportunityFilterTab,
	type OpportunityFilterObject,
} from './opportunityFilters';
import {
	getVisibleColumns,
	readColumnVisibility,
	writeColumnVisibility,
	OPPORTUNITY_COLUMNS,
	type OpportunityColumnName,
} from './opportunityColumns';
import type {OpportunityEdge} from './opportunityTypes';
import {
	toOpportunityTableRow,
	sessionSortToSortingState,
	sortingStateToSessionSort,
	type OpportunityTableRow,
	type SortingState,
} from './opportunityTableRows';
import {buildOpportunityColumns} from './opportunityColumnsView';
import {FilterWrapper} from '../shared/components/filters/FilterWrapper';
import {FILTER_SECTIONS} from '../shared/components/filters/dropdown_section';
import {FILTER_TYPE, FILTER_SECTION} from '../../constants';

// Opportunity-only fields live in the dedicated OpportunitiesList_project fragment (./OpportunitiesList)
// split out of the shared ProjectsLoader_project fragment (which the Projects list also consumes) so
// that list doesn't pay for / expose fields it never reads. It's spread below with
// @relay(mask: false), so no runtime import is needed here — same pattern as ProjectsLoader_project.
export const OpportunitiesPageQuery = graphql`
	query OpportunitiesPage_Query($loadSingleCurrency: Boolean!, $loadMultiCurrency: Boolean!) {
		viewer {
			id
			actualPersonId
			company {
				id
				currency
				skillLevelsEnabled
			}
			filters(first: 1000000) {
				edges {
					node {
						id
						name
						value
						section
						updatedAt
					}
				}
			}
			projects(first: 1000000, excludeRestricted: true) {
				edges {
					node {
						id
						...ProjectsLoader_project @relay(mask: false)
						...OpportunitiesList_project @relay(mask: false)
						...ProjectsLoader_financialsMultiCurrencies @include(if: $loadMultiCurrency) @relay(mask: false)
						...ProjectsLoader_financialsCompanyCurrency @include(if: $loadSingleCurrency) @relay(mask: false)
					}
				}
			}
		}
	}
`;

const PARAM = 'filter';

// Fixed-header page chrome (mirrors the All Invoices layout): a flex column that fills the routed content
// height, so the title / tabs / toolbar stay pinned and only the table area scrolls. 32px padding + a large
// gap between the header rows.
const PageWrapper = Styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 24px;
	padding: 32px;
`;

// The table's own scroll area: fills the remaining height below the fixed header so the inner CustomScrollDiv
// is bounded and the table scrolls independently (vertical + horizontal) without moving the header/toolbar.
const TableArea = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

// Borderless list chrome: the DS Table owns its own header + row separators, so this wrapper just gives it a
// white surface and nudges row text to the compact list size. Vertical scrolling (and virtualization) is
// driven by the table's own CustomScrollDiv (inside TableArea) via scrollRef.
//
// The DS Table renders a real <thead>/<th>/<td>; these wrapper-scoped selectors compile to
// `.thisWrapper thead th` (specificity 0,1,2), which outrank the DS cell's own single-class styles. Headers
// keep the DS Table's native bold, title-case styling — we only neutralise any inherited uppercasing.
const TableWrapper = Styled.div`
	background: #fff;

	/* Pin the table to a fixed 100% layout so the per-column width hints are authoritative. Without this an
	   auto-layout table lets a single long, unbroken name (no spaces to wrap on) blow the Opportunity Name
	   column out to that token's width, pushing the later columns off-screen and defeating the cell ellipsis.
	   With a fixed layout each column keeps its share of the full width and long names truncate (…) instead.

	   min-width floors that shared width. Under width:100% alone a narrow host — most visibly the client-detail
	   tab, whose side navigation steals horizontal room — keeps shrinking every column until the data (dates,
	   pills) is unreadable and the header labels collapse into each other, and the rightmost actions (kebab)
	   column is only reachable by squashing everything else. Flooring the width lets the table overflow instead
	   and the surrounding horizontal scroll area takes over; on wide screens width:100% still wins. The value is
	   the point below which the longest headers ("Opportunity Status", "Win Probability") would wrap past two
	   lines (or break mid-word) at their column share — keeping it here means wrapped headers stay at most two
	   clean lines, and everything narrower simply scrolls. */
	table {
		width: 100%;
		min-width: 1700px;
		table-layout: fixed;
	}

	thead th {
		text-transform: none;
	}
	/* The DS header cell forces white-space:nowrap, so a column narrower than its label lets the text overrun
	   the cell and collide with the next header (the columns' percentage hints starve the long labels —
	   "Opportunity Status", "Win Probability", "Opportunity Stage" — well before the table hits min-width).
	   Let header labels wrap onto a second line instead, and break an over-long single word rather than
	   overflow. Scoped to the header row via the wrapper class (…thead th div outranks the DS cell's own
	   single-class nowrap); data cells keep their single-line + ellipsis behaviour. */
	thead th div {
		white-space: normal;
		overflow-wrap: anywhere;
	}
	/* Row cells only: nudge the base font size to the compact list value. Scoped to the cell box (NOT a
	   descendant '*' selector) so the styled cells keep their own colours/weights — the ID badge's white
	   text, the status Pill's colours, the email Link, and the win-probability dot are all preserved. */
	tbody td {
		font-size: 13px;
	}
`;

// Compact in-list empty state, matching the Projects list's "no results in this view" look (centred
// message + the shared illustration). Deliberately NOT the full-page GenericEmptyState, whose absolute
// centring and mandatory CTA button break out of an unsized table container.
const EmptyState = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 56px 24px;
	text-align: center;
	.empty-state-message {
		font-size: 15px;
		color: #71717a;
	}
	img {
		width: 200px;
		max-width: 100%;
		margin-top: 32px;
	}
`;

// Column visibility uses the shared "eye" selector (TheEye) — the same dropdown the All Invoices page
// uses: an eye button that opens one row per non-mandatory column, each with an eye / eye-off toggle.
// The Opportunity name column is mandatory, so it is not offered here.
interface EyeColumnOption {
	name: OpportunityColumnName;
	translationId: string;
	checked: boolean;
}

const ColumnChooser = ({
	visibility,
	onChange,
}: {
	visibility: Record<OpportunityColumnName, boolean>;
	onChange: (v: Record<OpportunityColumnName, boolean>) => void;
}): JSX.Element => {
	const options: EyeColumnOption[] = OPPORTUNITY_COLUMNS.filter(c => c.name !== 'name').map(c => ({
		name: c.name,
		translationId: c.messageId,
		checked: visibility[c.name] !== false,
	}));
	// TheEye toggles the clicked option and hands back the full, updated option list as its 4th argument.
	const onSelect = (
		_option: EyeColumnOption,
		_parentOption: unknown,
		_grandParentOption: unknown,
		newOptions: EyeColumnOption[]
	): void => {
		const next = {...visibility};
		newOptions.forEach(o => {
			next[o.name] = o.checked;
		});
		onChange(next);
	};
	return <TheEye options={options} onSelect={onSelect} useDesignSystemButton expandLeft emphasis={EMPHASIS.MONO} />;
};

// The embeddable-component contract for reuse by other hosts. Every field is optional and defaults
// to the global /opportunities page's behaviour, so the existing page — which passes none of these — is
// unchanged. A host (e.g. the client-detail Opportunities tab) injects these to re-scope the same list.
export interface OpportunitiesListOptions {
	// Scope the list to a single client: rows are filtered to this client and the redundant Client column
	// is hidden. Undefined = the global, all-clients page.
	clientId?: string | null;
	// Storage scope for the tab / sort / column-visibility preferences. Defaults to the global
	// 'opportunities-overview' scope, reproducing the existing storage keys.
	preferenceScope?: string;
	// Whether the active tab is reflected in the URL query. True for the global page (bookmarkable
	// deep-links); the client tab passes false (URL↔tab sync is a global-page concern only).
	syncTabToUrl?: boolean;
	// Handler for the "+ New opportunity" button. The default opens the shared Create-Opportunity
	// modal wired for in-place refresh, which covers both list hosts; kept injectable for hosts that
	// need a different creation flow.
	onCreateOpportunity?: () => void;
	// Per-tab empty-state message ids. Defaults to the global copy; hosts override with client-specific copy.
	emptyStateMessageIds?: Record<OpportunityFilterTab, string>;
}

type Props = OpportunitiesListOptions & {
	viewer: {
		id: string;
		company: {id: string; currency: string; skillLevelsEnabled?: boolean | null};
		filters?: {edges?: Array<{node?: {id: string} | null} | null> | null} | null;
		projects?: {edges?: Array<OpportunityEdge | null> | null} | null;
	};
	retry: () => void;
};

export const OpportunitiesList = ({
	viewer,
	retry,
	clientId,
	preferenceScope = DEFAULT_PREFERENCE_SCOPE,
	syncTabToUrl = true,
	onCreateOpportunity,
	emptyStateMessageIds = OPPORTUNITY_EMPTY_MESSAGE_IDS,
}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const history = useHistory();
	const location = useLocation();
	const scrollRef = useRef(null);
	const [tableRef, table] = useTableFunctions();
	// Set to the new opportunity's id after a create; cleared once the row has been scrolled to.
	const [pendingHighlightId, setPendingHighlightId] = useState<string | null>(null);

	const canViewFinancials = hasSomePermission([
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION,
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
	]);
	const currency = viewer.company.currency;

	// URL is the source of truth for the active tab on the global page; a host with
	// syncTabToUrl=false (client tab) ignores the URL and relies on the session default only.
	// sessionStorage holds the last-used tab as the default when no URL param is present. Both
	// resolvers are pure + unit-tested. Preferences are read/written under the injected preferenceScope.
	const urlTab = syncTabToUrl ? new URLSearchParams(location.search).get(PARAM) : null;
	const [tab, setTabState] = useState<OpportunityFilterTab>(() => resolveInitialTab(urlTab, readSessionTab(preferenceScope)));
	const [visibility, setVisibility] = useState(() => readColumnVisibility(preferenceScope));
	const [search, setSearch] = useState('');
	// The shared FILTER_V4 panel hands back its selection as {project: {<filterType>: [ids]}}; we match it
	// client-side over the already-loaded rows (buildOpportunityFilterPredicate). null = no filter applied.
	const [filterObj, setFilterObj] = useState<OpportunityFilterObject>(null);
	const opportunityFilter = useMemo(() => buildOpportunityFilterPredicate(filterObj), [filterObj]);

	// The DS Table sorts internally; we only seed it (initialSorting) from the persisted session sort and
	// observe changes (onSortingChange) to persist them. Column ids equal their OpportunitySortKey, so the
	// translation to/from the {key, order} session shape is 1:1.
	const initialSorting = useMemo(
		() => sessionSortToSortingState(readSessionSort(preferenceScope) || {key: 'winChance', order: 'desc'}),
		[preferenceScope]
	);
	const handleSortingChange = (state: SortingState): void => {
		const sort = sortingStateToSessionSort(state);
		if (sort) writeSessionSort(sort, preferenceScope);
	};

	const setTab = (next: string) => {
		const t = next as OpportunityFilterTab;
		setTabState(t);
		writeSessionTab(t, preferenceScope);
		if (syncTabToUrl) {
			const params = new URLSearchParams(location.search);
			params.set(PARAM, t);
			history.replace({pathname: location.pathname, search: `?${params.toString()}`});
		}
	};
	const onColumnChange = (next: Record<OpportunityColumnName, boolean>) => {
		setVisibility(next);
		writeColumnVisibility(next, preferenceScope);
	};

	// Called by the create modal once the opportunity exists: refetch so the new row loads, queue it
	// for the highlight, and tell the modal whether the row will be visible where the user is standing.
	const onOpportunityCreated = (node: CreateOpportunityCreatedNode) => {
		retry();
		setPendingHighlightId(node.id);
		// A new opportunity always classifies as Open (win chance is 10–90%), so it is
		// visible only on the Open and All tabs.
		return {newRowVisible: tab === 'open' || tab === 'all'};
	};

	const handleCreate =
		onCreateOpportunity ??
		(() =>
			showCreateOpportunityModal({
				clientId: clientId ?? undefined,
				entryPoint: clientId ? 'client-page' : 'opportunities-page',
				onCreated: onOpportunityCreated,
			}));

	// Load-all + client-side win-probability filter. There is no server-side status arg, and
	// the tab logic needs projects across statuses (Won = 100% AND status ≠ OPPORTUNITY), so we do NOT
	// pre-filter by status here — filterOpportunitiesByTab applies the status/win-probability rules.
	// When clientId is set (client-detail tab), the same load-all payload is filtered to that
	// client in memory (Client has no projects connection in the schema).
	const projectEdges = useMemo<OpportunityEdge[]>(() => {
		const edges = (viewer.projects?.edges ?? []).filter((e): e is OpportunityEdge => Boolean(e && e.node));
		return clientId ? edges.filter(e => e.node.client?.id === clientId) : edges;
	}, [viewer.projects, clientId]);
	const counts = useMemo(
		() =>
			OPPORTUNITY_FILTER_TABS.reduce((acc, t) => {
				acc[t] = filterOpportunitiesByTab(projectEdges, t).length;
				return acc;
			}, {} as Record<OpportunityFilterTab, number>),
		[projectEdges]
	);
	// Filtered + mapped to the flat DS-Table row model; the table sorts these itself (no pre-sort here).
	const dataRows = useMemo<OpportunityTableRow[]>(() => {
		const inTab = filterOpportunitiesByTab(projectEdges, tab)
			.filter(e => matchesOpportunitySearch(e.node, search))
			.filter(e => opportunityFilter(e.node));
		return inTab.map(e => toOpportunityTableRow(e, intl, currency));
	}, [projectEdges, tab, search, opportunityFilter, intl, currency]);

	// After a create, the list refetches; scroll once the new row survives the active
	// tab/search/filter selection and is actually rendered — the design-system table
	// applies its built-in fade highlight on scrollToId, which no-ops for absent rows.
	// A queued id simply waits while the row is filtered out, and fires if the user later
	// switches to a tab or search where the row appears.
	useEffect(() => {
		if (pendingHighlightId && dataRows.some(row => row.id === pendingHighlightId)) {
			table.scrollToId(pendingHighlightId);
			setPendingHighlightId(null);
		}
	}, [pendingHighlightId, dataRows, table]);

	const visibleSet = useMemo(
		() =>
			new Set(
				getVisibleColumns({tab, visibility, canViewFinancials, hideClientColumn: clientId != null}).map(c => c.name)
			),
		[tab, visibility, canViewFinancials, clientId]
	);
	const columnElements = buildOpportunityColumns({
		formatMessage,
		isVisible: (name: OpportunityColumnName) => visibleSet.has(name),
		onOpenProject: (row: OpportunityTableRow) => history.push(row.projectUrlPath),
		onOpenClient: (row: OpportunityTableRow) => {
			const clientId = row.node.client?.id;
			if (clientId) history.push(`/clients/${clientId}`);
		},
		onMutationComplete: retry,
	});

	return (
		<PageWrapper data-cy="opportunities-page">
			<FlexRow flex="none" justifyContent="space-between" alignItems="center">
				<PageHeader>{formatMessage({id: 'opportunities.page_title'})}</PageHeader>
				{/* Hidden without the create-projects permission; the feature flag and module are
						    enforced at route registration. */}
				{hasPermission(PERMISSION_TYPE.PROJECTS_CREATE) && (
					<Button icon="plus" data-cy="new-opportunity-button" onPress={handleCreate}>
						{formatMessage({id: 'opportunities.new_button'})}
					</Button>
				)}
			</FlexRow>

			<Tabs value={tab} onValueChange={setTab}>
				{/* Tabs (left) + list toolbar (right) share one row. */}
				<FlexRow justifyContent="space-between" alignItems="center">
					<Tabs.List>
						{OPPORTUNITY_FILTER_TABS.map(t => (
							<Tabs.Tab key={t} value={t} data-cy={`opportunities-tab-${t}`}>
								{`${formatMessage({id: OPPORTUNITY_TAB_MESSAGE_IDS[t]})} (${counts[t]})`}
							</Tabs.Tab>
						))}
					</Tabs.List>
					{/* The toolbar controls are monochrome (grey border, dark icons) so the accent blue stays
							    reserved for the "New opportunity" CTA. All three default to medium emphasis, which
							    would render them in the accent colour, so each is pinned to mono explicitly. */}
					<FlexRow justifyContent="end" alignItems="center" gap="s">
						<SearchField
							value={search}
							onChange={setSearch}
							placeholder={formatMessage({id: 'common.search'})}
							data-cy="opportunities-search"
							emphasis={EMPHASIS.MONO}
						/>
						<ColumnChooser visibility={visibility} onChange={onColumnChange} />
						{/* Shared FILTER_V4 panel — the same one All Invoices uses. It self-loads its dropdown
								    data + self-portals; matching is done client-side over the loaded rows via
								    buildOpportunityFilterPredicate (see the rows memo above). Selections persist in
								    localStorage and, when saved, are scoped to FILTER_SECTION.OPPORTUNITIES. */}
						<FilterWrapper
							useDesignSystemButton
							emphasis={EMPHASIS.MONO}
							defaultSection={FILTER_SECTIONS.PROJECTS}
							filterByLabel={formatMessage({id: 'opportunities.page_title'})}
							filterSection={FILTER_SECTION.OPPORTUNITIES}
							appliedFiltersName="opportunities-overview-filters"
							viewer={viewer}
							projectFilters={[
								FILTER_TYPE.CLIENT,
								FILTER_TYPE.CLIENT_CONTACT,
								FILTER_TYPE.OPPORTUNITY_SALESPERSON,
								FILTER_TYPE.OPPORTUNITY_STAGE,
								FILTER_TYPE.OPPORTUNITY_WIN_PROBABILITY,
								FILTER_TYPE.OPPORTUNITY_STATUS,
							]}
							onFiltersChange={(obj: OpportunityFilterObject) => setFilterObj(obj)}
							cy="opportunities-filter"
						/>
					</FlexRow>
				</FlexRow>
			</Tabs>
			{/* The table's own bounded scroll area: fills the height below the fixed header, reveals both
					    thin custom scrollbars, and scrolls independently so the wide table no longer shifts the page. */}
			<TableArea>
				{dataRows.length === 0 ? (
					<EmptyState data-cy="opportunities-empty-state">
						<div className="empty-state-message">{formatMessage({id: emptyStateMessageIds[tab]})}</div>
						<img src={emptyStateBackground} alt="" aria-hidden="true" />
					</EmptyState>
				) : (
					<CustomScrollDiv
						horizontalandverticalcontent
						setScrollbarsRef={(node: HTMLElement | null) => {
							scrollRef.current = node as never;
						}}
					>
						<TableWrapper>
							<Table
								data={dataRows}
								width="100%"
								scrollRef={scrollRef}
								stickyHeader
								tableRef={tableRef}
								getRowId={(row: OpportunityTableRow) => row.id}
								initialSorting={initialSorting}
								onSortingChange={handleSortingChange}
								data-cy="opportunities-table"
							>
								{columnElements}
							</Table>
						</TableWrapper>
					</CustomScrollDiv>
				)}
			</TableArea>
		</PageWrapper>
	);
};

// The global /opportunities page: the reusable OpportunitiesList mounted with its global-page defaults
// (all-clients scope, URL-synced tabs, the 'opportunities-overview' preference scope, and the shared
// Create-Opportunity modal). Rendered by OpportunitiesPageQueryWrapper via ForecastQueryRenderer.
const OpportunitiesPage = ({viewer, retry}: {viewer: Props['viewer']; retry: () => void}): JSX.Element => (
	<OpportunitiesList viewer={viewer} retry={retry} />
);

export default OpportunitiesPage;
